import React, { useCallback, useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { API_URL, getToken } from "@/lib/api";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Lock, Loader2 } from "lucide-react";

// Worker dari pdfjs-dist via unpkg (cdnjs tidak host .mjs versi 5.x)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function PdfViewerModal({ fileId, title, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [error, setError] = useState(null);

  // Fetch PDF as blob (bukan langsung URL agar tidak gampang di-save dari Network tab/right-click)
  useEffect(() => {
    if (!fileId) return;
    let cancelled = false;
    const t = getToken();
    fetch(`${API_URL}/files/${fileId}/download`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch((e) => setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [fileId]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  // Disable Ctrl+S, Ctrl+P, Ctrl+Shift+S, F12 (devtools — best-effort)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && ["s", "p", "S", "P"].includes(e.key)) {
        e.preventDefault(); e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  const onDocLoad = useCallback(({ numPages }) => { setNumPages(numPages); setPage(1); }, []);
  const prev = () => setPage((p) => Math.max(1, p - 1));
  const next = () => setPage((p) => Math.min(numPages, p + 1));
  const zoomIn = () => setScale((s) => Math.min(2.5, s + 0.2));
  const zoomOut = () => setScale((s) => Math.max(0.5, s - 0.2));
  const blockContext = (e) => e.preventDefault();

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex flex-col" data-testid="pdf-viewer-modal" onContextMenu={blockContext}>
      {/* Header */}
      <header className="flex items-center justify-between px-5 py-3 bg-slate-900 text-white">
        <div className="flex items-center gap-3 min-w-0">
          <Lock className="w-4 h-4 text-amber-400 shrink-0" />
          <div className="min-w-0">
            <div className="font-semibold truncate" data-testid="pdf-viewer-title">{title || "Modul Ajar"}</div>
            <div className="text-xs text-amber-400/90" data-testid="pdf-viewer-notice">Modul ini view-only — tidak dapat di-download atau di-print</div>
          </div>
        </div>
        <button onClick={onClose} className="hover:bg-slate-800 rounded-lg p-2" data-testid="pdf-viewer-close" aria-label="Tutup">
          <X className="w-5 h-5" />
        </button>
      </header>

      {/* Toolbar */}
      <div className="flex items-center justify-center gap-2 px-5 py-2 bg-slate-800 text-white text-sm">
        <button onClick={prev} disabled={page <= 1} className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-30" data-testid="pdf-prev"><ChevronLeft className="w-4 h-4" /></button>
        <span className="font-mono text-xs" data-testid="pdf-pageinfo">{page} / {numPages || "—"}</span>
        <button onClick={next} disabled={page >= numPages} className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-30" data-testid="pdf-next"><ChevronRight className="w-4 h-4" /></button>
        <div className="w-px h-5 bg-slate-600 mx-2" />
        <button onClick={zoomOut} disabled={scale <= 0.5} className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-30" data-testid="pdf-zoom-out"><ZoomOut className="w-4 h-4" /></button>
        <span className="font-mono text-xs">{Math.round(scale * 100)}%</span>
        <button onClick={zoomIn} disabled={scale >= 2.5} className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-30" data-testid="pdf-zoom-in"><ZoomIn className="w-4 h-4" /></button>
      </div>

      {/* Viewer area — disable user-select & drag */}
      <div
        className="flex-1 overflow-auto bg-slate-700 p-6 select-none"
        style={{ WebkitUserSelect: "none", MozUserSelect: "none", msUserSelect: "none", userSelect: "none" }}
        onDragStart={(e) => e.preventDefault()}
      >
        {error && <div className="text-rose-300 text-center mt-10">Gagal memuat: {error}</div>}
        {!blobUrl && !error && (
          <div className="flex items-center justify-center text-slate-300 mt-10 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Memuat modul…
          </div>
        )}
        {blobUrl && (
          <div className="flex justify-center">
            <Document
              file={blobUrl}
              onLoadSuccess={onDocLoad}
              onLoadError={(e) => setError(e.message)}
              loading={<div className="text-slate-300">Memproses PDF…</div>}
            >
              <Page
                pageNumber={page}
                scale={scale}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                className="shadow-2xl"
              />
            </Document>
          </div>
        )}
      </div>
    </div>
  );
}
